import type React from 'react'
import { useState } from 'react'
import ReportProblemRounded from '@mui/icons-material/ReportProblemRounded'
import { AppDialog, type AppDialogAction } from './AppDialog'
import { Button } from './Button'
import { useNls } from '../../nls/nlsProvider'

export interface LeaveWorkspaceConfirmModalProps {
  open: boolean
  workspaceId: string
  workspaceName: string
  isLastOwner?: boolean
  isGuardianOnly?: boolean
  onClose: () => void
  onConfirm: () => Promise<unknown>
  onLeft?: () => void | Promise<void>
  onNavigateToOwners?: () => void
}

/**
 * Shared Leave Workspace confirmation modal (TM22, B2). UI only.
 * Displays full explanation of data loss and rights retention.
 * Handles sole owner and guardian refusals with explanatory words and link to owners.
 */
export const LeaveWorkspaceConfirmModal: React.FC<LeaveWorkspaceConfirmModalProps> = ({
  open,
  workspaceName,
  isLastOwner = false,
  isGuardianOnly = false,
  onClose,
  onConfirm,
  onLeft,
  onNavigateToOwners,
}) => {
  const { t } = useNls()
  const [busy, setBusy] = useState(false)
  const [errorText, setErrorText] = useState<string | null>(null)
  const [isSoleOwnerRefusal, setIsSoleOwnerRefusal] = useState(isLastOwner)
  const [isGuardianRefusal, setIsGuardianRefusal] = useState(isGuardianOnly)

  const handleConfirmLeave = async () => {
    setBusy(true)
    setErrorText(null)
    setIsSoleOwnerRefusal(false)
    setIsGuardianRefusal(false)

    try {
      await onConfirm()
      onClose()
      if (onLeft) {
        await onLeft()
      }
    } catch (caught: unknown) {
      const err = caught as { message?: string; details?: { code?: string } } | undefined
      const code = err?.details?.code
      if (code === 'GUARDIAN_ONLY') {
        setIsGuardianRefusal(true)
        setErrorText(t('NLSLeaveWorkspaceGuardianRefusal'))
      } else if (code === 'SOLE_OWNER') {
        setIsSoleOwnerRefusal(true)
        setErrorText(t('NLSLeaveWorkspaceSoleOwnerRefusal'))
      } else {
        setErrorText(err?.message || t('NLSLeaveWorkspaceFailedGeneric'))
      }
    } finally {
      setBusy(false)
    }
  }

  const title = t('NLSLeaveWorkspaceConfirmTitle', { workspace: workspaceName })
  const defaultBody = t('NLSLeaveWorkspaceConfirmBody', { workspace: workspaceName })

  const actions: AppDialogAction[] = [
    {
      label: t('NLSConfirmActionCancel'),
      variant: 'ghost',
      onClick: onClose,
      disabled: busy,
    },
    ...(!isSoleOwnerRefusal && !isGuardianRefusal
      ? [
          {
            label: t('NLSLeaveWorkspaceAction'),
            variant: 'danger' as const,
            onClick: handleConfirmLeave,
            loading: busy,
            disabled: busy,
          },
        ]
      : []),
  ]

  return (
    <AppDialog
      open={open}
      size="sm"
      tone="danger"
      icon={<ReportProblemRounded />}
      title={title}
      onDismiss={onClose}
      actions={actions}
      data-testid="leave-workspace-confirm-modal"
    >
      <div className="flex flex-col gap-3 py-1 text-sm text-[var(--text)]">
        <p>{defaultBody}</p>

        {errorText ? (
          <div
            role="alert"
            className="p-3 rounded-lg border border-[var(--danger)] bg-[var(--danger-soft)] text-[var(--danger-strong)] flex flex-col gap-2"
          >
            <span>{errorText}</span>
            {isSoleOwnerRefusal && onNavigateToOwners ? (
              <div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    onClose()
                    onNavigateToOwners()
                  }}
                >
                  {t('NLSLeaveWorkspaceManageOwnersLink')}
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </AppDialog>
  )
}
